<script lang="ts">
    let { onLoading } = $props();

    import {
        firebaseSignInWithIdToken,
        safeSignOut,
        trackEvent,
    } from "@services/firebase";
    import { createUserIfNoExists } from "@services/common";
    import { SocialLogin } from "@capgo/capacitor-social-login";
    import { Capacitor } from "@capacitor/core";
    import { onMount } from "svelte";
    import { translate } from "@/utils/format";
    import { wait } from "@/utils/common";
    import { goTo } from "@/utils/router";

    let errorMessage = $state("");
    let isWeb = $state(false);

    const GOOGLE_CLIENT_ID =
        "207500226795-pap3om90g7pi6pdet9h7spjcapg3eq2l.apps.googleusercontent.com";
    const IOS_CLIENT_ID =
        "207500226795-1jncfvvo5deor3de5vvgkaut52m2dmft.apps.googleusercontent.com";

    onMount(async () => {
        isWeb = Capacitor.getPlatform() === "web";

        if (isWeb) {
            // Para web, inicializar Google Identity Services (GSI)
            initGoogleGSI();
        } else {
            // Para móvil nativo, usar Capacitor Social Login
            await SocialLogin.initialize({
                google: {
                    iOSClientId: IOS_CLIENT_ID,
                    webClientId: GOOGLE_CLIENT_ID,
                },
            });
        }
    });

    function initGoogleGSI() {
        // Esperar a que google.accounts esté disponible
        if (typeof window.google === "undefined" || !window.google?.accounts) {
            console.warn("⚠️ Google Identity Services no está cargado todavía");
            // Reintentar después de 500ms
            setTimeout(initGoogleGSI, 500);
            return;
        }

        try {
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleGoogleCredentialResponse,
                auto_select: false,
                cancel_on_tap_outside: true,
            });
        } catch (error) {
            console.error("❌ Error inicializando Google GSI:", error);
        }
    }

    async function handleGoogleCredentialResponse(response: any) {
        if (!response?.credential) {
            console.error("❌ No se recibió credential de Google");
            errorMessage = translate(
                "ERROR_GENERIC_GOOGLE",
                "Ha ocurrido un error al iniciar sesión con Google.",
            );
            onLoading(false);
            return;
        }

        try {
            onLoading(true);
            console.log("--> Google GSI credential received");

            const res = await firebaseSignInWithIdToken(
                response.credential,
                "google.com",
            );

            // console.log(
            //     "--> firebaseSignInWithIdToken result: ",
            //     JSON.stringify(res),
            // );

            await createUserIfNoExists({ ...res, uid: res.localId });
            trackEvent("sign_up", { method: "google" });
        } catch (e) {
            console.error("❌ Error procesando Google credential:", e);
            errorMessage = translate(
                "ERROR_GENERIC_GOOGLE",
                "Ha ocurrido un error al iniciar sesión con Google.",
            );
            await safeSignOut();
            goTo("/logout", { cause: "firebase_error" });
        } finally {
            onLoading(false);
        }
    }

    async function loginWithGoogle() {
        errorMessage = "";
        onLoading(true);

        try {
            if (isWeb) {
                // Usar Google Identity Services para web
                if (
                    typeof window.google === "undefined" ||
                    !window.google?.accounts
                ) {
                    throw new Error("Google Identity Services no disponible");
                }

                // Mostrar el prompt de One Tap o el selector de cuentas
                window.google.accounts.id.prompt((notification: any) => {
                    if (
                        notification.isNotDisplayed() ||
                        notification.isSkippedMoment()
                    ) {
                        console.log("⚠️ One Tap no mostrado, usando popup");
                        // Si One Tap no se muestra, abrir el diálogo de selección manual
                        // Esto evita el problema de COOP porque usa el nuevo GSI
                        onLoading(false);
                    }
                });
            } else {
                // Usar Capacitor Social Login para móvil nativo
                let timeoutId: ReturnType<typeof setTimeout>;

                const timeout = new Promise((_resolve, reject) => {
                    timeoutId = setTimeout(
                        () => reject(new Error("timeout")),
                        60000,
                    );
                });

                const { result } = await Promise.race([
                    SocialLogin.login({
                        provider: "google",
                        options: {
                            prompt: "select_account",
                        },
                    }),
                    timeout,
                ]);

                console.log(
                    "--> SocialLogin.login result: ",
                    JSON.stringify(result),
                );
                clearTimeout(timeoutId);
                await wait(500);

                const res = await firebaseSignInWithIdToken(
                    result.idToken,
                    "google.com",
                );

                // console.log(
                //     "--> firebaseSignInWithIdToken result: ",
                //     JSON.stringify(res),
                // );

                await createUserIfNoExists({ ...res, uid: res.localId });
                trackEvent("sign_up", { method: "google" });
                onLoading(false);
            }
        } catch (e) {
            console.error("❌ Error loginWithGoogle:", e);

            if (e.message === "timeout") {
                errorMessage = translate(
                    "ERROR_TIMEOUT",
                    "La operación ha tardado demasiado. Intenta de nuevo.",
                );
            } else {
                errorMessage = translate(
                    "ERROR_GENERIC_GOOGLE",
                    "Ha ocurrido un error al iniciar sesión con Google.",
                );
                await safeSignOut();
                goTo("/logout", { cause: "firebase_error" });
            }
            onLoading(false);
        }
    }
</script>

<button class="googleOauth" onclick={loginWithGoogle}>
    <i class="fab fa-google"></i>
    <p>{translate("Sign in with Google", "Continuar con Google")}</p>
</button>

{#if errorMessage}
    <p class="error">{errorMessage}</p>
{/if}

<style lang="scss">
    @use "./styles/index.scss";
</style>
